import axios from "axios";
import { Voucher } from "../pages/nfts/store/nfts.reducer";
import { axiosInstance } from "./api";
import { Gallery } from "@/types/galleryData";
import { SpaceData } from "@/types/spaceData";
import { Artwork, PublicArtworkEmbed } from "@/types/artworksData";
import { getProxiedImageUrl } from "./imageProxy";
import {
  A9Response,
  SketchfabAssetOrderDirection,
  SketchfabAssetsResponse,
  SketchfabCollectionsResponse,
  SketchfabImportJobStatus,
  SketchfabOAuthStatus,
} from "@/types/sketchfabData";
import {
  ArtworkWorkspaceArtworkDetail,
  ArtworkWorkspaceArtworkListResponse,
  ArtworkWorkspaceConfig,
  ArtworkWorkspaceSketchfabAsset,
} from "@/types/artworkWorkspace";
import { ExternalSound } from "@/types/soundsData";

export type UpsertMoodResponse = {
  status: boolean;
  moodIndex?: number;
  mood?: any;
  moods?: any[];
};

export const ApiClient = function (baseAPI: string) {
  const isOfflineReplayResponse = (headers: any): boolean => {
    return String(headers?.["x-artetra-offline-replay"] || headers?.["X-Artetra-Offline-Replay"] || "").toLowerCase() === "true";
  };

  const isNetworkError = (error: any): boolean => {
    return Boolean(error?.request && !error?.response);
  };

  const errorHandler = function (error: any) {
    const _error = error as any;
    return {
      data: {
        status: false,
        msg: _error.response?.data?.error?.message || _error.response?.data?.message || _error.message,
        code: _error.response?.data?.error?.code || "INTERNAL",
        details: _error.response?.data?.data || _error.response?.data?.error?.details || null,
      },
    };
  };

  return {
    getEuropeanaItemImages: async (recordId: string) => {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/europeana/record/images`, {
          params: {
            recordId
          }
        });
        return data;
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    searchEuropeanaSounds: async (params: { query: string; page?: number; pageSize?: number }) => {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/europeana/search-sounds`, {
          params: {
            query: params.query,
            ...(params.page != null ? { page: params.page } : {}),
            ...(params.pageSize != null ? { pageSize: params.pageSize } : {}),
          },
        });
        if (data.status && data.data) {
          const result = data.data;
          return {
            status: true as const,
            sounds: (result.sounds ?? []) as ExternalSound[],
            totalResults: result.totalResults ?? 0,
            itemsCount: result.itemsCount ?? 0,
          };
        }
        return {
          status: false as const,
          sounds: [] as ExternalSound[],
          totalResults: 0,
          itemsCount: 0,
        };
      } catch (error) {
        console.log(error);
        return {
          status: false as const,
          sounds: [] as ExternalSound[],
          totalResults: 0,
          itemsCount: 0,
        };
      }
    },
    getGalleryPreview: async (provider: string, collectionId: string) => {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/galleries/preview/`, {
          params: {
            source: provider,
            collectionId
          }
        })
        return data;
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    changeGalleryDiorama: async (galleryId: string, artworkId: string) => {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/gallerysetup/diorama/${galleryId}/${artworkId}`, {});
        return data.status;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    upsertMood: async (spaceId: string, mood: any, index: number): Promise<UpsertMoodResponse> => {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/spaces/${spaceId}/moods`, {
          mood, index
        })

        return {
          ...(data?.data || {}),
          status: data?.status === true,
        };
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    upload3Dfiles: async (folder: string, files: File[]): Promise<{ name: string, url: string }[]> => {
      try {
        const normalizedFolder = folder.replace(/\/+$/, "");
        const formData = new FormData();
        formData.append("folder", normalizedFolder);
        files.forEach(file => {
          formData.append("files", file);
        });
        const { data } = await axiosInstance.post(`${baseAPI}/3d-files`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })

        if (data.status) {
          return data.urls
        } else {
          return [];
        }
      } catch (error) {
        console.log(error);
        return []
      }
    },
    get3DFileUrls: async (folder: string): Promise<string[]> => {
      try {
        const normalizedFolder = folder.replace(/\/+$/, "");
        const { data } = await axiosInstance.get(`${baseAPI}/3d-files?folder=${normalizedFolder}`);
        if (data.status) return data.urls;
        return [];
      } catch (error) {
        console.log(error);
        return [];
      }
    },
    sendEnquire: async (email: string, content: string, artworkId: string) => {
      try {
        const { data } = await axios.post(`${baseAPI}/artworks/enquire`, {
          email, content, artworkId
        })

        return { status: true, data: data };
      } catch (error) {
        console.log(error);
        return { status: false, data: error.response.data };
      }
    },
    sendContactUs: async (name: string, email: string, message: string): Promise<A9Response<{ message: string }>> => {
      try {
        const { data } = await axios.post(`${baseAPI}/contact`, {
          name, email, message
        })

        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: { message: "" },
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Failed to send message",
          },
        };
      }
    },
    autoPopulateArtworks: async (
      source: string,
      galleryId: string,
      artworkIds: any,
      startSlot: number,
      placementMode?: "singleSlot" | "spreadSlots"
    ) => {
      try {
        let autopopulateURL = "";
        if (source === "" || source === "artetra") {
          autopopulateURL = `${baseAPI}/artworks/autopopulate`;
        } else if (source === "europeana") {
          autopopulateURL = `${baseAPI}/europeana/autopopulate`;
        } else if (source === "sketchfab") {
          autopopulateURL = `${baseAPI}/sketchfab/autopopulate`;
        } else {
          return { status: false };
        }

        const { data } = await axiosInstance.post(autopopulateURL, {
          galleryId,
          artworkIds,
          startSlot,
          ...(placementMode ? { placementMode } : {}),
        })

        return data;
      } catch (error) {
        console.log(error);
        const responseData = (error as any)?.response?.data;
        return responseData || {
          status: false,
          message: (error as any)?.message || "Failed to auto populate artworks",
        };
      }
    },
    changeGalleryTitle: async (galleryId: string, newTitle: string) => {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/gallerysetup/title/${galleryId}`, {
          newTitle
        })

        return data;
      } catch (error) {
        return { status: false, error: error.response.data }
      }
    },
    listGalleryLabels: async function () {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/gallery-labels`);
        if (data?.status === false) {
          return { status: false, labels: [] as Array<{ _id: string; name: string }> };
        }
        return {
          status: true,
          labels: Array.isArray(data?.labels) ? data.labels : [],
        };
      } catch (error) {
        console.log(error);
        return { status: false, labels: [] as Array<{ _id: string; name: string }> };
      }
    },
    /** PUT /galleries/:galleryId/labels — body `{ labelIds: string[] }` (empty array clears). Seller + gallery access. */
    setGalleryLabels: async function (galleryId: string, labelIds: string[]) {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/galleries/${galleryId}/labels`, {
          labelIds,
        });
        return data;
      } catch (error: any) {
        const payload = error?.response?.data;
        return {
          status: false
        };
      }
    },
    moveArtworks: async (galleryId: string, artworksToMove: any[]) => {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/artworks/position/move`, {
          galleryId,
          artworksToMove
        })

        return data;
      } catch (error) {
        console.log(error);
        return { status: false }
      }
    },
    getOriginalImage: async (artworkId: string) => {
      try {
        const { data } = await axios.get(`${baseAPI}/artwork/image/${artworkId}`);
        return data;
      } catch (error) {
        console.log(error);
        return { status: false }
      }
    },
    publishGallery: async (galleryId: string, publish: boolean) => {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/gallerysetup/publish/${galleryId}`, {
          publish: publish ? 1 : 0
        })

        return data
      } catch (error) {
        console.log(error);
        return { status: false }
      }
    },
    shareFeedabck: async (content: string, userId: string) => {
      try {
        const { data } = await axios.post(`${baseAPI}/feedbacks`, {
          feedback: content, userId
        })

        return data;
      } catch (error) {
        console.log(error);
        return { status: false }
      }
    },
    getDioramaArtworks: async () => {
      try {
        const { data } = await axios.get(`${baseAPI}/artworks/general`)
        return data;
      } catch (error) {
        console.log(error);
        return { status: false }
      }
    },
    setSpawningPoint: async (galleryId: string, position: any, rotation: any) => {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/gallerysetup/spawning/${galleryId}`, {
          rotation, position
        })

        return data;
      } catch (error) {
        console.log(error);
        return { status: false }
      }
    },
    changeMoodForGallery: async (galleryId: string, moodIndex: number) => {
      try {
        const { data } = await axiosInstance.post(`${baseAPI}/gallerysetup/moods/${moodIndex}/${galleryId}`, {})

        if (data.status) {
          return data
        }
        return { status: false };
      } catch (error) {
        console.log(error);
        return { status: false }
      }
    },
    muteGallery: async (galleryId: string, muted: boolean = true) => {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/sounds/${galleryId}/mute`, {
          muted: muted ? "muted" : "unmuted"
        });
        if (data.status) {
          return data;
        }
        return { status: false };
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    changeSoundsForGallery: async (galleryId: string, soundId: string) => {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/sounds/${galleryId}/${soundId}`, {});
        if (data.status) {
          return data;
        }
        return { status: false };
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getSoundsForGallery: async (galleryId: string) => {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/sounds/${galleryId}`);
        if (data.status) {
          return data;
        }
        return { status: false, data: { sounds: [] } };
      } catch (error) {
        console.log(error);
        return { status: false, data: { sounds: [] } };
      }
    },
    createSoundForGallery: async (galleryId: string, formData: FormData) => {
      try {
        const { data } = await axiosInstance.post(`${baseAPI}/sounds/${galleryId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data.status) {
          return data;
        }
        return { status: false };
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    /** POST body `{ name, url }`; backend creates sound and sets it as gallery background. Returns `{ status: true }`. */
    importEuropeanaSoundForGallery: async (
      galleryId: string,
      payload: { name: string; url: string; sourceName: string; sourceId: string }
    ) => {
      try {
        const { data } = await axiosInstance.post(
          `${baseAPI}/sounds/${galleryId}/europeana`,
          {sound: payload}
        );
        if (data.status) {
          return data;
        }
        return { status: false };
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    removeSoundFromGallery: async (galleryId: string, soundId: string) => {
      try {
        const { data } = await axiosInstance.delete(`${baseAPI}/sounds/${galleryId}/${soundId}`);
        return data;
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    addGalleryCollaborator: async (galleryId: string, email: string, role: "editor" | "contributor") => {
      try {
        const { data } = await axiosInstance.post(`${baseAPI}/galleries/${galleryId}/collaborators`, {
          email,
          role,
        });
        return data;
      } catch (error) {
        console.log(error);
        return { status: false, error: (error as any)?.response?.data };
      }
    },
    updateGalleryCollaboratorRole: async (galleryId: string, userId: string, role: "editor" | "contributor") => {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/galleries/${galleryId}/collaborators/${userId}`, {
          role,
        });
        return data;
      } catch (error) {
        console.log(error);
        return { status: false, error: (error as any)?.response?.data };
      }
    },
    removeGalleryCollaborator: async (galleryId: string, userId: string) => {
      try {
        const { data } = await axiosInstance.delete(`${baseAPI}/galleries/${galleryId}/collaborators/${userId}`);
        return data;
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getMe: async function () {
      try {
        const { data } = await axiosInstance.post(`${baseAPI}/auth/me`, {})
        if (data.status) {
          return data
        }

        return { status: false, user: null, statusCode: null }
      } catch (error) {
        console.log(error);
        return {
          status: false,
          user: null,
          statusCode: (error as any)?.response?.status ?? null,
        }
      }
    },
    resizeArtwork: async function (artworkId: string, height: number) {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/artwork/${artworkId}`, {
          height
        })

        if (data.status) {
          return data.data;
        }
      } catch (error) {
        return false
      }
    },
    putArtworkOnSale: async function (artworkId: string, galleryId: string, targetSlot: number, listing: boolean) {
      try {
        const { data } = await axiosInstance.post(`${baseAPI}/artworks/listing`, {
          artworkId, galleryId, targetSlot, listing
        });
        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return (error as any)?.response?.data || { status: false };
      }
    },
    setArtworkWrapperActiveArtwork: async function (galleryId: string, wrapperId: string, artworkId: string) {
      try {
        const { data } = await axiosInstance.put(
          `${baseAPI}/galleries/${galleryId}/artwork-wrappers/${wrapperId}/active-artwork`,
          { artworkId }
        );
        return data;
      } catch (error) {
        console.log(error);
        return (error as any)?.response?.data || { status: false };
      }
    },
    updatePseudoMomIntroSettings: async function (
      galleryId: string,
      settings: { durationSeconds: number; introSoundId?: string | null; autoRotateObjects?: boolean; manualRotateObjects?: boolean }
    ) {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/galleries/${galleryId}/pseudo-mom-intro`, settings);
        return data;
      } catch (error) {
        console.log(error);
        return (error as any)?.response?.data || { status: false };
      }
    },
    updatePseudoMomIntroDuration: async function (galleryId: string, durationSeconds: number) {
      return this.updatePseudoMomIntroSettings(galleryId, { durationSeconds });
    },
    getDefaultMoMGallery: async function () {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/galleries/momdefault`);
        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getDefaultMoMGalleryByAdmin: async function (includeUnpublished: boolean = true) {
      try {
        const query = includeUnpublished ? "?includeUnpublished=1" : "";
        const { data } = await axiosInstance.get(`${baseAPI}/admin/galleries/mom${query}`);
        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getMoMGalleryByUserId: async function () {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/galleries/mom`);
        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getSellerMomGallery: async function () {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/galleries/sellermom`);
        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getGalleriesByUser: async function () {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/galleries/owner`);
        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    editHexaProcGallery: async function (galleryId: string, spaceToModify: number, open = 1, wrapperIds = []) {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/gallery/hexaproc/${galleryId}`, {
          spaceToModify, open, wrapperIds
        })

        return data;
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    }
    ,
    // ALEX: user can use this endpoint for save gallery and space modification by seller.
    updateGalleryBySeller: async function (galleryId: string, gallery: Partial<Gallery>, space: SpaceData) {
      try {
        return await axiosInstance.put(`${baseAPI}/gallery/${galleryId}`, {
          gallery, space
        })
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getGalleryById: async function (galleryId: string) {
      try {
        const { data, headers } = await axiosInstance.get(`${baseAPI}/gallery/${galleryId}`);
        if (data.status) {
          return {
            ...data,
            __offlineReplay: isOfflineReplayResponse(headers),
          };
        }
        return { status: false, networkError: false };
      } catch (error) {
        console.log(error);
        return { status: false, networkError: isNetworkError(error) };
      }
    },
    getSpaceById: async function (spaceId: string) {
      try {
        const { data, headers } = await axiosInstance.get(`${baseAPI}/space/${spaceId}`);
        if (data.status) {
          return {
            ...data,
            __offlineReplay: isOfflineReplayResponse(headers),
          };
        }
        return { status: false, networkError: false };
      } catch (error) {
        console.log(error);
        return { status: false, networkError: isNetworkError(error) };
      }
    },
    getArtworksByUser: async function () {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/artworks/owner`);
        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getArtworksByGallery: async function (galleryId: string) {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/artworks/gallery/${galleryId}`);
        if (data.status) {
          return data;
        }

        return { status: false, networkError: false };
      } catch (error) {
        console.log(error);
        return { status: false, networkError: isNetworkError(error) };
      }
    },
    getArtworkById: async function (artworkId: string) {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/artwork/${artworkId}`);
        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getArtworkPublicById: async function (
      artworkId: string
    ): Promise<A9Response<PublicArtworkEmbed>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/artwork/public/${artworkId}`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: {
            id: "",
            listed: true,
            downloadEnabled: false,
            title: null,
            artistName: null,
            description: null,
            type: null,
            media: { dataUrl: null, modelUrl: null, thumbnailUrl: null, mimeType: null, directModelEligible: false },
            edm: {
              viewerStrategy: "direct-media",
              landingPageUrl: null,
              oembedDiscoveryUrls: [],
              edmRightsUri: null,
              metadataTierCurrent: null,
              contentTierCurrent: null,
            },
            source: null,
          },
          error: {
            code: "NOT_FOUND",
            message: e.data?.msg || "Not found",
          },
        };
      }
    },
    login: async function (email: string, password: string) {
      try {
        const { data } = await axiosInstance.post(`${baseAPI}/auth/signin`, {
          email,
          password,
        });

        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    listedVouchersRequest: async function () {
      try {
        return await axiosInstance.get(`${baseAPI}/voucher`);
      } catch (error) {
        return errorHandler(error);
      }
    },
    lazyMintRequest: async function (payload: Voucher) {
      try {
        return await axiosInstance.post(`${baseAPI}/voucher`, payload);
      } catch (error) {
        return errorHandler(error);
      }
    },
    listNftRequest: async function (payload: {
      voucherId: string;
      slotPosition: string;
    }) {
      try {
        const { voucherId, slotPosition } = payload;
        return await axiosInstance.put(`${baseAPI}/voucher/${voucherId}`, {
          position: slotPosition,
        });
      } catch (error) {
        console.log(error);
        return errorHandler(error);
      }
    },
    buyNftRequest: async function (payload: {
      voucherId: string;
      newOwner: string;
      hash: string;
    }) {
      try {
        const { voucherId, newOwner, hash } = payload;
        return await axiosInstance.post(`${baseAPI}/voucher/${voucherId}`, {
          address: newOwner,
          hash,
        });
      } catch (error) {
        return errorHandler(error);
      }
    },
    unlistedNftsRequest: async function (payload: string) {
      try {
        return await axios.get(`${baseAPI}/nft/${payload}`);
      } catch (error) {
        return errorHandler(error);
      }
    },
    activateUserRoles: async function (payload: {
      role: string;
      activate: number;
    }) {
      try {
        const { role, activate } = payload;

        const data = await axiosInstance.put(
          `${baseAPI}/users/activate-roles`,
          { role: role, activate: activate },
        );
        localStorage.setItem("accessToken", data.data.jwt)
        return data;
      } catch (error) {
        return errorHandler(error);
      }
    },
    saveExternalSources: async function (externalSources: string[]) {
      try {
        const { data } = await axiosInstance.post(`${baseAPI}/users/external_sources`, {
          externalSources
        });
        return data;
      } catch (error) {
        return errorHandler(error);
      }
    },
    getSketchfabOAuthStart: async function (): Promise<A9Response<{ authorizeUrl: string }>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/sketchfab/oauth/start`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: { authorizeUrl: "" },
          error: {
            code: "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    getSketchfabOAuthStatus: async function (): Promise<A9Response<SketchfabOAuthStatus>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/sketchfab/oauth/status`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: {
            connected: false,
            quotaMode: "limited",
            importLimit: null,
            effectiveImportLimit: 10,
            currentImportedCount: 0,
            remainingImportSlots: 0,
            canImport: false,
          },
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    disconnectSketchfabOAuth: async function (): Promise<A9Response<{ disconnected: boolean }>> {
      try {
        const { data } = await axiosInstance.delete(`${baseAPI}/sketchfab/oauth/disconnect`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: { disconnected: false },
          error: {
            code: "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    getSketchfabCollections: async function (): Promise<A9Response<SketchfabCollectionsResponse>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/sketchfab/me/collections`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: { collections: [] },
          error: {
            code: "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    getSketchfabAssets: async function (
      cursor?: string,
      limit: number = 25,
      query: string = "",
      orderDirection: SketchfabAssetOrderDirection = "desc",
      collectionUid?: string
    ): Promise<A9Response<SketchfabAssetsResponse>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/sketchfab/me/assets`, {
          params: {
            cursor,
            limit,
            query,
            orderDirection,
            collectionUid,
          },
        });
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: { assets: [], nextCursor: null },
          error: {
            code: "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    getArtworkWorkspaceSketchfabAssets: async function (
      params?: {
        cursor?: string;
        limit?: number;
        search?: string;
        orderDirection?: SketchfabAssetOrderDirection;
        collectionUid?: string;
      }
    ): Promise<A9Response<{ assets: ArtworkWorkspaceSketchfabAsset[]; nextCursor: string | null }>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/artworks/workspace/sketchfab/assets`, {
          params,
        });
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: { assets: [], nextCursor: null },
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    getArtworkWorkspaceConfig: async function (): Promise<A9Response<ArtworkWorkspaceConfig>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/artworks/workspace/config`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: {
            capabilities: {
              canUseSketchfabImport: false,
              canUseDatasets: false,
              canEditAdvancedEdm: false,
              canPublish: false,
              canValidateMedia: false,
              canBatchCreate: false,
            },
            visibleTabs: [],
            batchTemplateVersion: "",
            datasets: [],
            defaultInstitutionId: null,
          },
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    getArtworkWorkspaceArtworks: async function (
      params?: {
        page?: number;
        limit?: number;
        search?: string;
        sort?: string;
        sortBy?: string;
        sortDirection?: "asc" | "desc";
        datasetKey?: string;
        recordState?: string;
        type?: string;
        institutionId?: string;
      }
    ): Promise<A9Response<ArtworkWorkspaceArtworkListResponse>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/artworks/workspace/artworks`, {
          params,
        });
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: {
            items: [],
            total: 0,
            page: params?.page || 1,
            limit: params?.limit || 24,
          },
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    getArtworkWorkspaceArtwork: async function (
      artworkId: string
    ): Promise<A9Response<ArtworkWorkspaceArtworkDetail>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/artworks/workspace/artworks/${artworkId}`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: {} as ArtworkWorkspaceArtworkDetail,
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    deleteArtworkWorkspaceArtwork: async function (
      artworkId: string
    ): Promise<A9Response<{ artworkId: string }>> {
      try {
        const { data } = await axiosInstance.delete(`${baseAPI}/artworks/workspace/artworks/${artworkId}`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: { artworkId },
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    downloadArtworkWorkspaceBatchTemplate: async function (): Promise<{
      status: boolean;
      blob: Blob | null;
      fileName: string;
      error?: {
        code: string;
        message: string;
      };
    }> {
      try {
        const response = await axiosInstance.get(`${baseAPI}/artworks/workspace/batch/template`, {
          responseType: "blob",
        });
        const headerContent = response.headers["content-disposition"] || response.headers["Content-Disposition"] || "";
        const fileNameMatch = /filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i.exec(String(headerContent));
        const fileName = fileNameMatch?.[1]
          ? decodeURIComponent(fileNameMatch[1].replace(/^UTF-8''/i, ""))
          : "artwork-workspace-batch-template.xlsx";

        return {
          status: true,
          blob: response.data as Blob,
          fileName,
        };
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          blob: null,
          fileName: "artwork-workspace-batch-template.xlsx",
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    validateArtworkWorkspaceArtworkMedia: async function (
      artworkId: string
    ): Promise<A9Response<Record<string, unknown>>> {
      try {
        const { data } = await axiosInstance.post(`${baseAPI}/artworks/workspace/artworks/${artworkId}/validate-media`, {});
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: {},
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    createSketchfabImportJob: async function (payload: {
      mode: "selected" | "all" | "collection";
      sourceIds?: string[];
      chunkSize?: number;
      cursor?: string;
      collectionUid?: string;
    }): Promise<A9Response<SketchfabImportJobStatus>> {
      try {
        const { data } = await axiosInstance.post(`${baseAPI}/sketchfab/import/jobs`, payload);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: {
            jobId: "",
            status: "failed",
            counters: {
              requested: 0,
              processed: 0,
              imported: 0,
              skippedDuplicates: 0,
              skippedNonDownloadable: 0,
              skippedLimitReached: 0,
              failed: 1,
            },
            errorsSample: [],
          },
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    getSketchfabImportJob: async function (
      jobId: string
    ): Promise<A9Response<SketchfabImportJobStatus>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/sketchfab/import/jobs/${jobId}`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: {
            jobId: "",
            status: "failed",
            counters: {
              requested: 0,
              processed: 0,
              imported: 0,
              skippedDuplicates: 0,
              skippedNonDownloadable: 0,
              skippedLimitReached: 0,
              failed: 1,
            },
            errorsSample: [],
          },
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    getLatestSketchfabImportJob: async function (): Promise<A9Response<SketchfabImportJobStatus>> {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/sketchfab/import/jobs/latest`);
        return data;
      } catch (error) {
        const e = errorHandler(error);
        return {
          status: false,
          data: {
            jobId: "",
            status: "failed",
            counters: {
              requested: 0,
              processed: 0,
              imported: 0,
              skippedDuplicates: 0,
              skippedNonDownloadable: 0,
              skippedLimitReached: 0,
              failed: 1,
            },
            errorsSample: [],
          },
          error: {
            code: e.data?.code || "INTERNAL",
            message: e.data?.msg || "Request failed",
          },
        };
      }
    },
    editArtwork: async function (artworkId: string, artwork: Artwork) {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/artworks/edit/${artworkId}`, artwork);
        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    searchCollections: async function (
      source: string,
      collectionId: string,
      options: { qf?: string[]; includeSounds?: boolean } = {}
    ) {
      try {
        let searchURL = "";
        if (source === "europeana") {
          searchURL = `${baseAPI}/europeana/search-collection/${encodeURIComponent(collectionId)}`;
        } else if (source === "sketchfab") {
          searchURL = `${baseAPI}/sketchfab/search-collection/${encodeURIComponent(collectionId)}`;
        } else {
          return { status: false };
        }

        const params = new URLSearchParams();
        if (source === "europeana") {
          (options.qf || []).forEach((filter) => params.append("qf", filter));
          if (options.includeSounds) params.append("includeSounds", "true");
        }

        const { data } = await axiosInstance.get(searchURL, {
          params: params.toString() ? params : undefined
        });
        if (data.status) {
          return data;
        } else {
          return { status: false };
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    searchItem: async function (source: string, itemId: string, includeSounds: boolean = false) {
      try {
        let searchURL = "";
        if (source === "europeana") {
          searchURL = `${baseAPI}/europeana/search-item`;
        } else {
          return { status: false };
        }

        const { data } = await axiosInstance.get(searchURL, {
          params: {
            itemId,
            ...(source === "europeana" && includeSounds ? { includeSounds: true } : {})
          }
        });
        if (data.status) {
          return data;
        } else {
          return data || { status: false };
        }
      } catch (error) {
        console.log(error);
        return (error as any)?.response?.data || { status: false };
      }
    },
    searchExtenralArtworks: async function (source: string, filter: string, includeSounds: boolean = false) {
      try {
        let searchURL = "";
        if (source === "europeana") {
          searchURL = `${baseAPI}/artworks/europeana/search-convert`;
        } else if (source === "sketchfab") {
          searchURL = `${baseAPI}/sketchfab/search-convert`;
        }
        else {
          return { status: false };
        }

        const { data } = await axiosInstance.get(searchURL, {
          params: {
            query: filter,
            ...(source === "europeana" && includeSounds ? { includeSounds: true } : {})
          }
        });

        if (data.status) {
          return data;
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    listExternalArtwork: async function (source: string, artwork: any, galleryId: string, slot: number) {
      try {
        let listURL = "";
        if (source === "europeana") {
          listURL = `${baseAPI}/artworks/europeana/list-artwork`;
        } else if (source === "sketchfab") {
          listURL = `${baseAPI}/sketchfab/list-artwork`;
        } else {
          return { status: false };
        }

        const { data } = await axiosInstance.post(listURL, {
          artwork,
          galleryId,
          slot
        });

        if (data.status) {
          return {
            status: true,
            wrapperId: data.data.wrapperId,
            artwork: data.data.artwork,
            wrapper: data.data.wrapper,
            stats: data.data.stats
          };
        } else {
          return { ...data, status: false, wrapperId: null, artwork: null };
        }
      } catch (error) {
        console.log(error);
        const responseData = (error as any)?.response?.data;
        return responseData
          ? { ...responseData, status: false, wrapperId: null, artwork: null }
          : {
            status: false,
            wrapperId: null,
            artwork: null,
            message: (error as any)?.message || "Failed to list artwork",
          };
      }
    },
    getArtworksByUserId: async function (userId: string) {
      try {
        const { data } = await axiosInstance.get(`${baseAPI}/admin/artworks/user/${userId}`);
        if (data.status) {
          return data;
        } else {
          return { status: false };
        }
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },

    changeDioramaSlot: async function (galleryId: string, slot: number) {
      try {
        const { data } = await axiosInstance.put(`${baseAPI}/admin/diorama/slot/${galleryId}`, {
          slotTarget: slot
        });
        return data;
      } catch (error) {
        console.log(error);
        return { status: false };
      }
    },
    getProxiedImageUrl: (url: string) => {
      return getProxiedImageUrl(url, baseAPI);
    },
    downloadArtworkAttachment: async function (artworkId: string, attachmentId: string) {
      try {
        const response = await axiosInstance.get(`${baseAPI}/artworks/attachment/${artworkId}/${attachmentId}`,
          {responseType: 'arraybuffer'}
        );
        return response;
      } catch (error) {
        console.log(error);
        return null;
      }
    }
  };
};

// Gallery    
